import { Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatChip, MatChipRemove } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import { Subject } from 'rxjs';
import { UserEffects } from '../../../../state/user/user.effects';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import { IDateRangePickerDate } from '../../date-range-picker/date-range-picker.interface';
import { IDropdownOption, TDropdownId } from '../../mat-dropdown/mat-dropdown.component';
import { FilterCardFieldInputComponent } from '../filter-card-field-input/filter-card-field-input.component';
import { TFilterCardFieldConfig } from '../filter-card.interface';

/** A single committed filter — a compact chip in `'view'` mode, the same input used to add it
 * (pre-filled) when clicked into `'edit'` mode. Mirrors digital-boards' `AdvancedFilterItemComponent`.
 * A `dateRange` chip additionally renders navigation arrows directly on the collapsed chip (also
 * mirroring digital-boards) — see `shiftDateRange`. */
@Component({
  imports: [FilterCardFieldInputComponent, MatChip, MatChipRemove, MatIcon, MatTooltip, TranslateModule],
  selector: 'app-filter-card-chip',
  styleUrl: './filter-card-chip.component.scss',
  templateUrl: './filter-card-chip.component.html',
})
export class FilterCardChipComponent<Name extends string> implements OnInit {
  public readonly field = input.required<TFilterCardFieldConfig<Name>>();
  public readonly value = input.required<unknown>();
  /** The manager's shared `oneOpenFormAtATime$` — see its doc comment. `null` is a safe no-op default
   * for any future consumer that doesn't wire it up. */
  public readonly oneOpenFormAtATime$ = input<Subject<symbol> | null>(null);
  public readonly remove = output<void>();
  public readonly edit = output<unknown>();

  public readonly mode = signal<'view' | 'edit'>('view');
  /** This chip's own broadcast identity — stable for the component instance's lifetime (which, since
   * `@for` tracks by `field.name`, spans exactly as long as the chip itself exists). */
  private readonly identity = Symbol();
  private readonly destroyRef = inject(DestroyRef);
  public readonly label = computed(() => {
    const field = this.field();
    const value = this.value();

    switch (field.type) {
      case 'buttonToggle': {
        const option = field.options.find((candidate) => candidate.value === value);

        return option ? this.translate.instant(option.labelKey) : field.label;
      }
      case 'dropdown': {
        const options = (value as IDropdownOption<TDropdownId>[] | null) ?? [];

        // Always "label (n)", single selection included, so every dropdown chip reads the same way and
        // the chip width doesn't jump with the selected option's name.
        return options.length ? `${field.label} (${options.length})` : field.label;
      }
      case 'dateRange': {
        const range = value as IDateRangePickerDate | null;

        if (!range?.startDate || !range?.endDate) {
          return field.label;
        }

        // `label = value`, matching digital-boards' date chip. There the `=` comes from the filter's
        // SQL operator; this filter card has no operator model (see docs/FILTER_CONTEXT.md §1), so it is
        // literal.
        //
        // A range that still matches one of the picker's named phrases is shown by name ("Today",
        // "This Week"), the way digital-boards does — except that there the phrase is what's stored,
        // while here the value is always a concrete range and the phrase is re-derived from it. So a
        // nudge with the chip's own arrows just stops matching and falls back to the dates (or lands
        // on the neighbouring phrase, e.g. Today → Yesterday).
        const namedRange = DateUtilitiesService.convertRangeIntoDatePickerOption(range);

        if (namedRange) {
          return `${field.label} = ${this.translate.instant(`system.label.dateRanges.${namedRange}`)}`;
        }

        const { dateFormat } = UserEffects.getUser().configuration;

        return `${field.label} = ${range.startDate.format(dateFormat)} - ${range.endDate.format(dateFormat)}`;
      }
    }
  });

  private readonly translate = inject(TranslateService);

  public ngOnInit(): void {
    // Deferred to `ngOnInit` (rather than subscribing in the constructor) because `oneOpenFormAtATime$`
    // is an input — reading it before Angular has bound the real value would silently subscribe to the
    // default `null` instead.
    this.oneOpenFormAtATime$()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((identity) => {
        // Own broadcast (`chipClick` below) always arrives here too — `identity === this.identity`
        // then, so this correctly skips closing itself.
        if (identity !== this.identity && this.mode() === 'edit') {
          this.mode.set('view');
        }
      });
  }

  /**
   * Broadcasting imperatively here — rather than from an `effect()` watching for `mode() === 'edit'`
   * — is deliberate, not a style choice: an `effect()` only sees the *value at flush time*, not every
   * transition. `oneOpenFormAtATime$.next(...)` is synchronous and re-entrant — a subscriber (e.g. the
   * add-form, still reacting to an earlier, not-yet-flushed 'picking' transition) can synchronously
   * write signals of its own before this effect would ever get a chance to run. That raced the two
   * broadcasts in testing: the add-form's queued effect flushed a second time, closed this chip via
   * the listener above, and only *then* did this chip's own 'edit'-watching effect finally flush —
   * by which point `mode` had already moved on to 'view' again, so the broadcast for entering edit
   * mode never happened at all. Firing it here, synchronously with the write, closes that race.
   */
  public chipClick(): void {
    this.mode.set('edit');
    this.oneOpenFormAtATime$()?.next(this.identity);
  }

  public commitEdit(value: unknown): void {
    this.mode.set('view');
    this.edit.emit(value);
  }

  /**
   * Shifts a `dateRange` chip's value by one period without entering edit mode — day-count/month/year,
   * matching whichever granularity the current range spans (same algorithm as
   * `DateRangePickerComponent.shiftDateRange`, which governs the same field's in-picker arrows; kept
   * in sync deliberately so shifting from the chip and shifting from an open picker behave identically
   * for the same value). Like any other chip edit, this only updates the pending chip value via the
   * `edit` output — it does not force-submit the card (kamishibai's own toolbar arrows are the one
   * documented exception to that rule; this isn't a second one).
   */
  public shiftDateRange(direction: 1 | -1): void {
    const range = this.value() as IDateRangePickerDate | null;

    if (!range?.startDate || !range?.endDate) {
      return;
    }

    this.edit.emit(this.computeShiftedRange(range.startDate, range.endDate, direction));
  }

  private computeShiftedRange(startDate: Dayjs, endDate: Dayjs, direction: 1 | -1): IDateRangePickerDate {
    const shiftMethod: 'add' | 'subtract' = direction === 1 ? 'add' : 'subtract';
    const isFullYear =
      startDate.isSame(startDate.clone().startOf('year'), 'day') &&
      endDate.isSame(endDate.clone().endOf('year'), 'day');
    const isFullMonth = startDate.date() === 1 && endDate.isSame(endDate.clone().endOf('month'), 'day');

    if (isFullYear) {
      return {
        endDate: endDate.clone()[shiftMethod](1, 'year').endOf('year'),
        startDate: startDate.clone()[shiftMethod](1, 'year').startOf('year'),
      };
    }

    if (isFullMonth) {
      return {
        endDate: endDate.clone()[shiftMethod](1, 'month').endOf('month'),
        startDate: startDate.clone()[shiftMethod](1, 'month').startOf('month'),
      };
    }

    const dayCount = Math.max(1, endDate.diff(startDate, 'day', true));

    return {
      endDate: endDate.clone()[shiftMethod](dayCount, 'day'),
      startDate: startDate.clone()[shiftMethod](dayCount, 'day'),
    };
  }
}
